import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "../database.generated";
import type { Page, PageRequest, PersistedFile, RepositoryResult } from "../contracts";
import { SupabaseFileRepository } from "./file-repository";
import { notFound, validationFailure } from "./repository-helpers";

export interface HostedSourceScope {
  organisationId: string;
  auditEngagementId: string;
  reportId: string;
  reportVersionId: string;
}

/** An AAL2 user client supplies all reads and private Storage operations. */
export class HostedSourceFileBoundary {
  private readonly files: SupabaseFileRepository;
  constructor(private readonly client: SupabaseClient<Database>) { this.files = new SupabaseFileRepository(client); }

  private async reportExists(scope: HostedSourceScope): Promise<boolean> {
    const report = await this.client.from("reports").select("id").eq("id", scope.reportId)
      .eq("organisation_id", scope.organisationId).eq("audit_engagement_id", scope.auditEngagementId).maybeSingle();
    if (report.error || !report.data) return false;
    const version = await this.client.from("report_versions").select("id").eq("id", scope.reportVersionId)
      .eq("report_id", scope.reportId).eq("organisation_id", scope.organisationId).maybeSingle();
    return !version.error && Boolean(version.data);
  }

  private async eligible(scope: HostedSourceScope, file: PersistedFile): Promise<boolean> {
    if (file.organisationId !== scope.organisationId || file.bucketId !== "organisation-source-files") return false;
    if (file.auditEngagementId && file.auditEngagementId !== scope.auditEngagementId) return false;
    if (file.reportVersionId) {
      const version = await this.client.from("report_versions").select("report_id").eq("id", file.reportVersionId)
        .eq("organisation_id", scope.organisationId).maybeSingle();
      if (version.error || version.data?.report_id !== scope.reportId) return false;
    }
    if (file.reportSectionId) {
      const section = await this.client.from("report_sections").select("report_version_id").eq("id", file.reportSectionId)
        .eq("organisation_id", scope.organisationId).maybeSingle();
      if (section.error || !section.data) return false;
      const version = await this.client.from("report_versions").select("report_id").eq("id", section.data.report_version_id)
        .eq("organisation_id", scope.organisationId).maybeSingle();
      if (version.error || version.data?.report_id !== scope.reportId) return false;
    }
    return true;
  }

  async list(scope: HostedSourceScope, page: PageRequest): Promise<RepositoryResult<Page<PersistedFile>>> {
    if (!await this.reportExists(scope)) return notFound("Report and audit");
    const listed = await this.files.listForOrganisation(scope.organisationId, page);
    if (!listed.ok) return listed;
    const items: PersistedFile[] = [];
    for (const file of listed.value.items) if (file.status === "Available" && await this.eligible(scope, file)) items.push(file);
    return { ok: true, value: { items, nextCursor: listed.value.nextCursor } };
  }

  async uploadImage(scope: HostedSourceScope, file: File): Promise<RepositoryResult<PersistedFile>> {
    if (!await this.reportExists(scope)) return notFound("Report and audit");
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return validationFailure("Private screenshot must be PNG, JPEG or WebP.");
    const bytes = new Uint8Array(await file.arrayBuffer()), id = `source-image-${crypto.randomUUID()}`;
    const reserved = await this.files.reserveUpload({ id, organisationId: scope.organisationId,
      auditEngagementId: scope.auditEngagementId, logicalName: file.name, originalFilename: file.name,
      bucketId: "organisation-source-files", mimeType: file.type, bytes, idempotencyKey: `${id}-reserve` });
    if (!reserved.ok) return reserved;
    const uploaded = await this.files.uploadReservedObject(reserved.value, bytes);
    if (!uploaded.ok) return uploaded;
    return this.files.confirmUpload(reserved.value, bytes, `${id}-confirm`);
  }

  async open(scope: HostedSourceScope, fileId: string): Promise<RepositoryResult<{ url: string; expiresAt: string }>> {
    if (!await this.reportExists(scope)) return notFound("Report and audit");
    const metadata = await this.files.getMetadata(fileId);
    if (!metadata.ok) return notFound("Eligible private source file");
    if (!await this.eligible(scope, metadata.value) || !["Available", "Archived"].includes(metadata.value.status)) return notFound("Eligible private source file");
    return this.files.createDownloadGrant(fileId);
  }
}
